/**
 * Form primitives on React Aria Components — WCAG 2.2 AA, keyboard operable,
 * RTL-safe, locale-aware (UX-004 §8.1). Units and number/date formatting come
 * from props + the locale, never hardcoded (UX-003 §6.2).
 */
import { useId, type ReactNode } from "react";
import {
  TextField,
  NumberField,
  Select as AriaSelect,
  SelectValue,
  ListBox,
  ListBoxItem,
  Switch,
  RadioGroup,
  Radio,
  DateRangePicker,
  MenuTrigger,
  Menu,
  MenuItem,
  SearchField,
  Label,
  Input,
  Group,
  Button,
  Popover,
  Dialog,
  RangeCalendar,
  CalendarGrid,
  CalendarCell,
  Heading,
  DateInput,
  DateSegment,
  Text,
  FieldError,
  type Key,
  type Selection,
  type DateRangePickerProps,
  type DateValue,
} from "react-aria-components";

/* ── Field ──────────────────────────────────────────────────────────────────
   A generic labelled container for a custom control (the RAC-based inputs below
   carry their own label/description/error). */
export function Field(props: {
  label?: string;
  description?: string;
  errorMessage?: string;
  isRequired?: boolean;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="sk-field" data-invalid={props.errorMessage ? "true" : undefined}>
      {props.label ? (
        <span className="sk-field__label">
          {props.label}
          {props.isRequired ? <span aria-hidden="true"> *</span> : null}
        </span>
      ) : null}
      {props.children}
      {props.description ? (
        <span className="sk-field__desc">{props.description}</span>
      ) : null}
      {props.errorMessage ? (
        <span className="sk-field__error" role="alert">
          {props.errorMessage}
        </span>
      ) : null}
    </div>
  );
}

/* ── TextInput ──────────────────────────────────────────────────────────── */
export function TextInput(props: {
  label: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  description?: string;
  errorMessage?: string;
  placeholder?: string;
  type?: "text" | "email" | "search" | "url" | "tel";
  isRequired?: boolean;
  isDisabled?: boolean;
}): ReactNode {
  return (
    <TextField
      className="sk-field"
      value={props.value}
      defaultValue={props.defaultValue}
      onChange={props.onChange}
      type={props.type ?? "text"}
      isRequired={props.isRequired}
      isDisabled={props.isDisabled}
      isInvalid={!!props.errorMessage}
    >
      <Label className="sk-field__label">{props.label}</Label>
      <Input className="sk-input" placeholder={props.placeholder} />
      {props.description ? (
        <Text slot="description" className="sk-field__desc">
          {props.description}
        </Text>
      ) : null}
      <FieldError className="sk-field__error">{props.errorMessage}</FieldError>
    </TextField>
  );
}

/* ── NumberWithUnit ─────────────────────────────────────────────────────────
   The unit comes from configuration (a prop), never a literal in the component. */
export function NumberWithUnit(props: {
  label: string;
  unit: string;
  value?: number;
  defaultValue?: number;
  onChange?: (value: number) => void;
  minValue?: number;
  maxValue?: number;
  step?: number;
  formatOptions?: Intl.NumberFormatOptions;
  description?: string;
  errorMessage?: string;
  isDisabled?: boolean;
}): ReactNode {
  return (
    <NumberField
      className="sk-field"
      value={props.value}
      defaultValue={props.defaultValue}
      onChange={props.onChange}
      minValue={props.minValue}
      maxValue={props.maxValue}
      step={props.step}
      formatOptions={props.formatOptions}
      isDisabled={props.isDisabled}
      isInvalid={!!props.errorMessage}
    >
      <Label className="sk-field__label">{props.label}</Label>
      <Group className="sk-numfield">
        <Input className="sk-numfield__input" />
        <span className="sk-numfield__unit">{props.unit}</span>
      </Group>
      {props.description ? (
        <Text slot="description" className="sk-field__desc">
          {props.description}
        </Text>
      ) : null}
      <FieldError className="sk-field__error">{props.errorMessage}</FieldError>
    </NumberField>
  );
}

/* ── Select ─────────────────────────────────────────────────────────────── */
export interface SelectOption {
  key: string;
  label: string;
}

export function Select(props: {
  label: string;
  items: SelectOption[];
  selectedKey?: string | null;
  onSelectionChange?: (key: string) => void;
  description?: string;
  errorMessage?: string;
  placeholder?: string;
  isDisabled?: boolean;
}): ReactNode {
  return (
    <AriaSelect
      className="sk-field"
      selectedKey={props.selectedKey ?? null}
      onSelectionChange={(key: Key | null) => {
        if (key !== null) props.onSelectionChange?.(String(key));
      }}
      isDisabled={props.isDisabled}
      isInvalid={!!props.errorMessage}
      placeholder={props.placeholder}
    >
      <Label className="sk-field__label">{props.label}</Label>
      <Button className="sk-select__button">
        <SelectValue className="sk-select__value" />
        <span aria-hidden="true" className="sk-select__arrow">
          ▼
        </span>
      </Button>
      {props.description ? (
        <Text slot="description" className="sk-field__desc">
          {props.description}
        </Text>
      ) : null}
      <FieldError className="sk-field__error">{props.errorMessage}</FieldError>
      <Popover className="sk-popover">
        <ListBox className="sk-listbox" items={props.items}>
          {(item: SelectOption) => (
            <ListBoxItem
              id={item.key}
              className="sk-listbox__item"
              textValue={item.label}
            >
              {item.label}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </AriaSelect>
  );
}

/* ── MultiSelect ──────────────────────────────────────────────────────────────
   Several values from a fixed list. React Aria's Select is single-only, so this
   is a button + popover over a multiple-selection ListBox: keyboard operable,
   screen-reader labelled, and it stays open while you toggle items. The trigger
   summarises the selection as text (count, never colour). */
export function MultiSelect(props: {
  label: string;
  items: SelectOption[];
  selectedKeys: string[];
  onChange: (keys: string[]) => void;
  description?: string;
  errorMessage?: string;
  placeholder?: string;
  isDisabled?: boolean;
}): ReactNode {
  const labelId = useId();
  const descId = useId();
  const selected = new Set<Key>(props.selectedKeys);
  const chosen = props.items.filter((i) => selected.has(i.key));
  const summary =
    chosen.length === 0
      ? (props.placeholder ?? "Any")
      : chosen.length <= 2
        ? chosen.map((c) => c.label).join(", ")
        : `${chosen.length} selected`;

  function handle(keys: Selection): void {
    if (keys === "all") {
      props.onChange(props.items.map((i) => i.key));
    } else {
      props.onChange([...keys].map(String));
    }
  }

  return (
    <div className="sk-field" data-invalid={props.errorMessage ? "true" : undefined}>
      <span className="sk-field__label" id={labelId}>
        {props.label}
      </span>
      <MenuTrigger>
        <Button
          className="sk-select__button"
          isDisabled={props.isDisabled}
          aria-labelledby={labelId}
          aria-describedby={props.description ? descId : undefined}
        >
          <span className="sk-select__value" data-placeholder={chosen.length === 0 || undefined}>
            {summary}
          </span>
          <span aria-hidden="true" className="sk-select__arrow">
            ▼
          </span>
        </Button>
        <Popover className="sk-popover">
          <Menu
            className="sk-listbox"
            items={props.items}
            selectionMode="multiple"
            selectedKeys={selected}
            onSelectionChange={handle}
            aria-labelledby={labelId}
          >
            {(item: SelectOption) => (
              <MenuItem id={item.key} className="sk-listbox__item" textValue={item.label}>
                {item.label}
              </MenuItem>
            )}
          </Menu>
        </Popover>
      </MenuTrigger>
      {props.description ? (
        <span className="sk-field__desc" id={descId}>
          {props.description}
        </span>
      ) : null}
      {props.errorMessage ? (
        <span className="sk-field__error" role="alert">
          {props.errorMessage}
        </span>
      ) : null}
    </div>
  );
}

/* ── SearchInput ──────────────────────────────────────────────────────────────
   A labelled search box (React Aria SearchField): Escape and the clear button
   both empty it, and onChange fires as the user types. Debouncing, if wanted,
   belongs to the caller — a primitive shouldn't hide latency. */
export function SearchInput(props: {
  label: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
  placeholder?: string;
  description?: string;
  isDisabled?: boolean;
  /** Visually hide the label (still read by screen readers). */
  hideLabel?: boolean;
}): ReactNode {
  return (
    <SearchField
      className="sk-field sk-search"
      value={props.value}
      defaultValue={props.defaultValue}
      onChange={props.onChange}
      onSubmit={props.onSubmit}
      isDisabled={props.isDisabled}
    >
      <Label className={props.hideLabel ? "sk-visually-hidden" : "sk-field__label"}>
        {props.label}
      </Label>
      <Group className="sk-search__group">
        <span aria-hidden="true" className="sk-search__icon">
          ⌕
        </span>
        <Input className="sk-search__input" placeholder={props.placeholder} />
        <Button className="sk-search__clear" aria-label="Clear search">
          ✕
        </Button>
      </Group>
      {props.description ? (
        <Text slot="description" className="sk-field__desc">
          {props.description}
        </Text>
      ) : null}
    </SearchField>
  );
}

/* ── Toggle ─────────────────────────────────────────────────────────────── */
export function Toggle(props: {
  label: string;
  isSelected?: boolean;
  defaultSelected?: boolean;
  onChange?: (isSelected: boolean) => void;
  isDisabled?: boolean;
}): ReactNode {
  return (
    <Switch
      className="sk-toggle"
      isSelected={props.isSelected}
      defaultSelected={props.defaultSelected}
      onChange={props.onChange}
      isDisabled={props.isDisabled}
    >
      <span className="sk-toggle__track" aria-hidden="true">
        <span className="sk-toggle__thumb" />
      </span>
      <span className="sk-toggle__label">{props.label}</span>
    </Switch>
  );
}

/* ── Segmented ──────────────────────────────────────────────────────────────
   Single-select segmented control (a styled radio group — accessible by
   default). */
export function Segmented(props: {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  ariaLabel?: string;
}): ReactNode {
  return (
    <RadioGroup
      className="sk-segmented"
      value={props.value}
      onChange={props.onChange}
      aria-label={props.ariaLabel ?? props.label ?? "Options"}
      orientation="horizontal"
    >
      {props.label ? <Label className="sk-field__label">{props.label}</Label> : null}
      <div className="sk-segmented__items">
        {props.options.map((o) => (
          <Radio key={o.key} value={o.key} className="sk-segmented__item">
            {o.label}
          </Radio>
        ))}
      </div>
    </RadioGroup>
  );
}

/* ── DateRange ──────────────────────────────────────────────────────────────
   Period selection. Value/onChange use the RAC date types so formatting and
   locale come from the library, not from us. */
type DRProps = DateRangePickerProps<DateValue>;

export function DateRange(props: {
  label: string;
  value?: DRProps["value"];
  defaultValue?: DRProps["defaultValue"];
  onChange?: DRProps["onChange"];
  description?: string;
  errorMessage?: string;
  isDisabled?: boolean;
}): ReactNode {
  return (
    <DateRangePicker
      className="sk-field"
      value={props.value}
      defaultValue={props.defaultValue}
      onChange={props.onChange}
      isDisabled={props.isDisabled}
      isInvalid={!!props.errorMessage}
    >
      <Label className="sk-field__label">{props.label}</Label>
      <Group className="sk-daterange">
        <DateInput slot="start" className="sk-daterange__input">
          {(segment) => <DateSegment segment={segment} className="sk-daterange__seg" />}
        </DateInput>
        <span aria-hidden="true" className="sk-daterange__sep">
          –
        </span>
        <DateInput slot="end" className="sk-daterange__input">
          {(segment) => <DateSegment segment={segment} className="sk-daterange__seg" />}
        </DateInput>
        <Button className="sk-daterange__button" aria-label="Open calendar">
          📅
        </Button>
      </Group>
      {props.description ? (
        <Text slot="description" className="sk-field__desc">
          {props.description}
        </Text>
      ) : null}
      <FieldError className="sk-field__error">{props.errorMessage}</FieldError>
      <Popover className="sk-popover">
        <Dialog className="sk-daterange__dialog">
          <RangeCalendar className="sk-calendar">
            <header className="sk-calendar__head">
              <Button slot="previous" aria-label="Previous">
                ◀
              </Button>
              <Heading className="sk-calendar__heading" />
              <Button slot="next" aria-label="Next">
                ▶
              </Button>
            </header>
            <CalendarGrid className="sk-calendar__grid">
              {(date) => <CalendarCell date={date} className="sk-calendar__cell" />}
            </CalendarGrid>
          </RangeCalendar>
        </Dialog>
      </Popover>
    </DateRangePicker>
  );
}
